import { useCallback, useEffect, useState } from "react";
import { MIN_COL_WIDTH, MIN_ROW_HEIGHT } from "../constants";

/**
 * useResize
 * Drag-resize behaviour for column widths and row heights.  Writes the
 * new size back into the active sheet model via the setter passed in.
 */
export default function useResize({ activeSheet, colWidths, rowHeights, setSheetDataByName }) {
  const [resizeState, setResizeState] = useState(null);

  const startResize = useCallback((type, index, event) => {
    event.preventDefault();
    event.stopPropagation();
    setResizeState({
      type, index,
      start: type === "col" ? event.clientX : event.clientY,
      initial: type === "col" ? colWidths[index] : rowHeights[index],
    });
  }, [colWidths, rowHeights]);

  useEffect(() => {
    if (!resizeState) return undefined;

    const onMouseMove = (event) => {
      const delta = resizeState.type === "col"
        ? event.clientX - resizeState.start
        : event.clientY - resizeState.start;
      const nextSize = Math.max(
        resizeState.type === "col" ? MIN_COL_WIDTH : MIN_ROW_HEIGHT,
        resizeState.initial + delta,
      );

      setSheetDataByName((prev) => {
        const sheet = prev[activeSheet];
        if (!sheet) return prev;
        if (resizeState.type === "col") {
          const nextCols = [...sheet.colWidths];
          nextCols[resizeState.index] = nextSize;
          return { ...prev, [activeSheet]: { ...sheet, colWidths: nextCols } };
        }
        const nextRows = [...sheet.rowHeights];
        nextRows[resizeState.index] = nextSize;
        return { ...prev, [activeSheet]: { ...sheet, rowHeights: nextRows } };
      });
    };

    const onMouseUp = () => setResizeState(null);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [activeSheet, resizeState, setSheetDataByName]);

  return { startResize, resizeState };
}
